import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { products } from "@/lib/products";
import { Helmet } from "react-helmet";
import LazyImage from "@/components/ui/lazy-image";
import GoogleMap from "@/components/map/GoogleMap";

// Select first 3 products for featured section
const featuredProducts = products.slice(0, 3);

// Generate JSON-LD schema
const generateSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "TopMotorPart",
  "description": "Leading manufacturer and supplier of high-quality automotive parts. Global export of precision-engineered components meeting international standards.",
  "url": "https://topmotorpart.com",
  "logo": "/og-image.png",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "IN",
    "addressLocality": "GIFT City"
  }
});

const Index = () => {
  const getTruncatedDescription = (description: string) => {
    const firstSentence = description.split('.')[0] + '.';
    return firstSentence;
  };

  return (
    <Layout>
      <Helmet>
        <title>TopMotorPart - Premium Auto Parts Export</title>
        <meta name="description" content="Leading manufacturer and supplier of high-quality automotive parts. Global export of precision-engineered components meeting international standards." />
        <meta property="og:title" content="TopMotorPart - Premium Auto Parts Export" />
        <meta property="og:description" content="Leading manufacturer and supplier of high-quality automotive parts. Global export of precision-engineered components." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/og-image.png" />
        <meta property="og:url" content="https://topmotorpart.com" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TopMotorPart - Premium Auto Parts Export" />
        <meta name="twitter:description" content="Leading manufacturer and supplier of high-quality automotive parts." />
        <meta name="twitter:image" content="/og-image.png" />
        <meta name="theme-color" content="#ffffff" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="TopMotorPart" />
        <link rel="canonical" href="https://topmotorpart.com" />
        <link rel="apple-touch-icon" href="/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify(generateSchema())}
        </script>
      </Helmet>

      <section className="relative h-[80vh] bg-black" role="banner">
        <div className="absolute inset-0">
          <LazyImage
            src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7"
            alt="TopMotorPart hero image showing premium auto parts"
            className="object-cover w-full h-full"
            loading="eager"
            width={1920}
            height={1080}
          />
          <div className="absolute inset-0 hero-gradient" aria-hidden="true" />
        </div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="text-white max-w-2xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-4 md:mb-8 animate-fade-in">TopMotorPart</h1>
            <p className="text-lg md:text-xl mb-6 animate-fade-in opacity-90">
              Parts that Power Your Journey
            </p>
            <Button size="lg" asChild className="animate-fade-in">
              <Link to="/products">
                Explore Products <ArrowRight className="ml-2" size={16} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white relative z-20" aria-labelledby="about-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="about-heading" className="text-3xl font-bold mb-6">About Us</h2>
            <p className="text-lg text-gray-600 mb-6 max-w-3xl mx-auto">
              TopMotorPart is a leading supplier of high-quality aftermarket automotive parts,
              serving global markets with precision-engineered components that meet the highest
              standards of performance and reliability.
            </p>
            <Button variant="outline" asChild>
              <Link to="/about">Learn More About Us</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-gray-50" aria-labelledby="featured-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="featured-heading" className="text-3xl font-bold text-center mb-12">
            Featured Products
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {featuredProducts.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-lg shadow-lg overflow-hidden product-card-hover flex flex-col h-full"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <LazyImage
                    src={product.images[0]}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    width={400}
                    height={300}
                  />
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-semibold mb-2">{product.name}</h3>
                  <p className="text-gray-600 mb-4 flex-grow">
                    {getTruncatedDescription(product.description)}
                  </p>
                  <Button variant="outline" asChild className="w-full">
                    <Link to={`/products/${product.id}`}>View Details</Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center">
            <Button size="lg" asChild>
              <Link to="/products">
                View All Products <ArrowRight className="ml-2" size={16} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white" aria-labelledby="location-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="location-heading" className="text-3xl font-bold text-center mb-8">Our Location</h2>
          <div className="w-full h-[400px] rounded-lg overflow-hidden shadow-lg">
            <GoogleMap />
          </div>
        </div>
      </section>

      <section className="bg-primary py-16 md:py-20" aria-labelledby="cta-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 id="cta-heading" className="text-3xl font-bold text-white mb-8">
            Ready to Partner with Us?
          </h2>
          <Button
            size="lg"
            variant="secondary"
            className="bg-white text-primary hover:bg-gray-100"
            asChild
          >
            <Link to="/contact">Get a Quote Today</Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
